import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

/**
 * DSGVO-Consent. Wir speichern die Auswahl in localStorage statt in einem
 * Cookie — das vermeidet das Henne-Ei-Problem (man darf vor Consent eigentlich
 * keinen Cookie setzen). LocalStorage ist technisch erforderlich fuer die
 * Funktion „Praeferenzen merken" und damit ohne Consent zulaessig.
 *
 * Kategorien:
 *   essential   — immer an, nicht abwaehlbar (Session, Cookie-Consent selbst)
 *   preferences — Sprache, Theme, Layout-Praeferenzen
 *   statistics  — anonymisierte Nutzungsstatistik (aktuell nicht aktiv)
 *   marketing   — Drittinhalte mit Tracking (YouTube-Embeds, Discord-Widget)
 */
const STORAGE_KEY = 'shruby.consent.v1';

export interface ConsentState {
  essential:   true;            // ist immer wahr — typecheck
  preferences: boolean;
  statistics:  boolean;
  marketing:   boolean;
  decidedAt:   number | null;   // ms-timestamp; null = noch keine Entscheidung
}

function load(): ConsentState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults();
    const parsed = JSON.parse(raw);
    return {
      essential: true,
      preferences: !!parsed.preferences,
      statistics:  !!parsed.statistics,
      marketing:   !!parsed.marketing,
      decidedAt:   typeof parsed.decidedAt === 'number' ? parsed.decidedAt : null
    };
  } catch {
    return defaults();
  }
}

function defaults(): ConsentState {
  return { essential: true, preferences: false, statistics: false, marketing: false, decidedAt: null };
}

export const useConsentStore = defineStore('consent', () => {
  const state = ref<ConsentState>(load());

  const decided = computed(() => state.value.decidedAt !== null);
  const allowMarketing = computed(() => state.value.marketing);
  const allowStatistics = computed(() => state.value.statistics);
  const allowPreferences = computed(() => state.value.preferences);

  function persist() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.value));
  }

  function acceptAll() {
    state.value = { essential: true, preferences: true, statistics: true, marketing: true, decidedAt: Date.now() };
    persist();
  }

  function rejectAll() {
    state.value = { essential: true, preferences: false, statistics: false, marketing: false, decidedAt: Date.now() };
    persist();
  }

  function save(partial: Partial<Omit<ConsentState, 'essential' | 'decidedAt'>>) {
    state.value = {
      essential: true,
      preferences: partial.preferences ?? state.value.preferences,
      statistics:  partial.statistics  ?? state.value.statistics,
      marketing:   partial.marketing   ?? state.value.marketing,
      decidedAt: Date.now()
    };
    persist();
  }

  function reopen() {
    state.value.decidedAt = null;
    persist();
  }

  return { state, decided, allowMarketing, allowStatistics, allowPreferences, acceptAll, rejectAll, save, reopen };
});
